import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { Toast } from '@whelk/components';
import { MultiModalComponentData } from '@whelk/const';
import { CHAT_ID } from '@whelk/const/src/routerUrl';
import { FORM_ITEM_TYPE } from '@whelk/services/src/type/chat';
import { AppDispatch, store } from '@whelk/store';
import { addFile } from '@whelk/store/slices/uploadSlice';
import {
  checkLogin,
  focusInput,
  isMobileByUA,
  openLoginModal,
  trackCustomEvent,
} from '@whelk/utils';
import imageCompressor from '@whelk/utils/src/imageCompressor';
import { ALL_TYPE_LIST, ALL_TYPE_TO_UPPER_CASE } from '@whelk/const/src/robot';
import { createMultipartUpload } from '@whelk/utils/src/ossService';

const IMAGE_MAX_SIZE_BY_MB = 20;
const FILE_MAX_SIZE_BY_MB = 100;

const getImageDimensions = (
  file: Blob | MediaSource,
): Promise<{
  width: number;
  height: number;
}> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = () => {
      reject('error');
    };
  });
};

const useUpload = () => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const selectedChatID = useSearchParams()?.get(CHAT_ID) || '';

  const formatFileInfo = async (file: File, upload_type: 'click' | 'drag' | 'paste' = 'click') => {
    const file_size = file.size;
    const file_type = file.name?.split('.')?.pop()?.toLowerCase() || '';

    /************** 异常情况处理 文件类型 图片大小 文件大小 *******************/
    if (file_type && ALL_TYPE_LIST.indexOf(file_type) === -1) {
      Toast.show(`支持上传 ${FILE_MAX_SIZE_BY_MB}M 以内的文件（${ALL_TYPE_TO_UPPER_CASE}）`);
      trackCustomEvent({
        id: 'upload_file_refuse',
        file_size,
        file_type,
        upload_type,
        reason: 1,
        page: selectedChatID ? 'chat_page' : 'home_page',
      });
      return false;
    }
    if (/image*/.test(file.type)) {
      //  TODO common config
      // 图片类型 最大 20M
      const imgUploadSize = 1024 * 1024 * IMAGE_MAX_SIZE_BY_MB;
      if (file.size > imgUploadSize) {
        Toast.show(t('toast_image_exceeds_size_limit_try_another'));
        trackCustomEvent({
          id: 'upload_file_refuse',
          file_size,
          file_type,
          upload_type,
          reason: 2,
          page: selectedChatID ? 'chat_page' : 'home_page',
        });
        return false;
      }
    } else {
      const fileUploadSize = 1024 * 1024 * FILE_MAX_SIZE_BY_MB;
      if (file.size > fileUploadSize) {
        Toast.show(t('toast_exceed_size_limit'));
        trackCustomEvent({
          id: 'upload_file_refuse',
          file_size,
          file_type,
          upload_type,
          reason: 4,
          page: selectedChatID ? 'chat_page' : 'home_page',
        });
        return false;
      }
    }
    /************** 异常情况处理 文件类型 图片大小 文件大小 *******************/
    const fileInfo: MultiModalComponentData = {
      currentFileType: /image*/.test(file.type) ? FORM_ITEM_TYPE.IMG : FORM_ITEM_TYPE.FILE,
      fileName: file.name,
      fileUrl: file as any, // 文件类型不用转url
      realFile: file,
      uuid: uuidv4(),
      // loading: true,
      file: file,
      file_size,
      file_type,
      upload_type,
      height: 0,
      width: 0,
    };

    if (file && /image*/.test(file.type) && fileInfo.file) {
      try {
        // 图片压缩且转换类型
        const newFile = await imageCompressor(file);
        if (newFile) {
          fileInfo.file = newFile as File;
          fileInfo.fileName = (newFile as File).name;
          fileInfo.file_type = fileInfo.fileName?.split('.')?.pop()?.toLowerCase() || '';
        }
      } catch (error) {
        console.error(error);
      }
      fileInfo.fileUrl = URL.createObjectURL(fileInfo.file);
      const { width, height } = await getImageDimensions(fileInfo.file);
      fileInfo.width = width ?? 0;
      fileInfo.height = height ?? 0;
    }
    return fileInfo;
  };

  const onBeforeUpload = async (
    file: File,
    fileList: File[],
    upload_type: 'click' | 'drag' | 'paste' = 'click',
  ) => {
    if (fileList.length + store.getState().upload.files.length > 10) {
      Toast.show(t('toast_upload_max_10'));
      return false;
    }

    if (!checkLogin()) {
      openLoginModal();
      return;
    }

    if (store.getState().upload.files.length >= 10) {
      Toast.show(t('toast_upload_max_10'));
      return false;
    }

    const fileInfo: MultiModalComponentData | false = await formatFileInfo(file, upload_type);
    if (!fileInfo) return;

    dispatch(addFile(fileInfo));
    await createMultipartUpload({
      fileInfo: fileInfo,
      selectedChatID: selectedChatID,
      dispatch: dispatch as AppDispatch,
    });
    if (!isMobileByUA) {
      focusInput();
    }
    return false; // prevent auto upload
  };
  const retryUpload = async (fileInfo: MultiModalComponentData, selectedChatID: string) => {
    dispatch(addFile(fileInfo));
    await createMultipartUpload({
      fileInfo: fileInfo,
      selectedChatID: selectedChatID,
      dispatch: dispatch as AppDispatch,
    });
  };
  return {
    onBeforeUpload,
    retryUpload,
  };
};

export default useUpload;
